import { Cell, FletchyVault, Note } from "./types";

export interface SearchHit {
  noteId: string;
  score: number;
  excerpt: string;
}

function normalizeTerm(value: string): string {
  return value.trim().toLowerCase();
}

function includesTerm(value: string, term: string): boolean {
  return normalizeTerm(value).includes(term);
}

function excerptFor(cells: Cell[], term: string): string {
  const matchingCell = cells.find((cell) => includesTerm(cell.content, term));
  if (!matchingCell) {
    return "";
  }

  const content = matchingCell.content.replace(/\s+/g, " ").trim();
  const index = normalizeTerm(content).indexOf(term);
  const start = Math.max(0, index - 52);
  const end = Math.min(content.length, index + term.length + 96);
  return content.slice(start, end);
}

export function noteText(vault: FletchyVault, note: Note): string {
  const cells = vault.cells.filter((cell) => note.cellIds.includes(cell.id));
  return [note.title, note.tags.join(" "), ...cells.map((cell) => cell.content)].join(" ");
}

export function searchVault(vault: FletchyVault, query: string): SearchHit[] {
  const term = normalizeTerm(query);
  const activeNotes = vault.notes.filter((note) => !note.trashedAt);

  if (!term) {
    return activeNotes
      .map((note) => ({ noteId: note.id, score: note.favorite ? 2 : 1, excerpt: "" }))
      .sort((a, b) => b.score - a.score);
  }

  return activeNotes
    .map((note) => {
      const cells = vault.cells.filter((cell) => note.cellIds.includes(cell.id));
      let score = 0;

      if (includesTerm(note.title, term)) {
        score += 25;
      }

      for (const tag of note.tags) {
        if (includesTerm(tag, term)) {
          score += 12;
        }
      }

      for (const cell of cells) {
        if (includesTerm(cell.content, term)) {
          score += 4;
        }
      }

      if (score > 0 && note.favorite) {
        score += 1;
      }

      return {
        noteId: note.id,
        score,
        excerpt: excerptFor(cells, term),
      };
    })
    .filter((hit) => hit.score > 0)
    .sort((a, b) => b.score - a.score);
}
